/**
 * File: useADRState.ts
 * Description: Custom hook to manage ADR definition and suggestion state to reduce component complexity
 * Author: GitHub Copilot
 * Created: 2025-01-XX
 * Last Modified: 2025-01-XX
 */

import { useState, useCallback } from 'react';

import { ADRDefinition, ADRSuggestion } from '../../types/adr';
import { ProjectItem } from '../../types/project';

// Constants
const ERROR_MESSAGE_TIMEOUT_MS = 5000;

interface ADRSuggestionData {
  inputs: {
    projectId: number;
    projectContext: string;
    maxSuggestions: number;
    timestamp: string;
    userInitiated: boolean;
  } | null;
  outputs: {
    suggestions: ADRSuggestion[];
    rawResponse?: string;
    processingTime?: number;
    timestamp: string;
    error?: string;
  } | null;
}

interface ADRGenerationData {
  inputs: {
    projectId: number;
    adrDefinition: ADRDefinition;
    timestamp: string;
    userInitiated: boolean;
  } | null;
  outputs: {
    success: boolean;
    contentGenerated?: boolean;
    processingTime?: number;
    timestamp: string;
    error?: string;
    generatedContent?: string;
    contentLength?: number;
    adrStatus?: string | null;
  } | null;
}

/**
 * Hook to manage ADR state (definitions, suggestions, generation states)
 */
export const useADRState = () => {
  // Project state
  const [project, setProject] = useState<ProjectItem | null>(null);

  // State for AI suggestions from adrService
  const [suggestions, setSuggestions] = useState<ADRSuggestion[]>([]);
  const [isGeneratingSuggestions, setIsGeneratingSuggestions] = useState(false);
  const [isAcceptingAll, setIsAcceptingAll] = useState(false);
  const [suggestionProjectContext, setSuggestionProjectContext] = useState<string>('');

  // State for capturing ADR suggestion inputs and outputs for feedback
  const [adrSuggestionData, setAdrSuggestionData] = useState<ADRSuggestionData>({
    inputs: null,
    outputs: null,
  });

  // State for capturing ADR generation inputs and outputs for feedback
  const [adrGenerationData, setAdrGenerationData] = useState<{
    [adrId: string]: ADRGenerationData;
  }>({});

  // State for ADR definitions
  const [definitions, setDefinitions] = useState<ADRDefinition[]>([]);
  const [editingDefinition, setEditingDefinition] = useState<ADRDefinition | null>(null);
  const [isLoadingDefinitions, setIsLoadingDefinitions] = useState(true);

  // State for generating ADRs
  const [isGeneratingADRs, setIsGeneratingADRs] = useState(false);
  const [generatingADRIds, setGeneratingADRIds] = useState<Set<string>>(new Set());

  // Monaco editor state for ADR editing
  const [editingADR, setEditingADR] = useState<ADRDefinition | null>(null);
  const [adrContent, setAdrContent] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date>(new Date());
  const [justSaved, setJustSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Modal states
  const [showDefinitionModal, setShowDefinitionModal] = useState(false);
  const [showADRViewer, setShowADRViewer] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [regenerateADRId, setRegenerateADRId] = useState<string | null>(null);

  // Error handling state
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showError, setShowError] = useState(false);

  // Form state for definition creation/editing
  const [formData, setFormData] = useState({
    title: '',
    llmInstructions: '',
    contextDescription: '',
  });

  /**
   * Show error message with auto-hide
   */
  const showErrorMessage = useCallback((message: string) => {
    setErrorMessage(message);
    setShowError(true);
    // Auto-hide after ERROR_MESSAGE_TIMEOUT_MS
    const timeoutId = setTimeout(() => {
      setShowError(false);
      setErrorMessage('');
    }, ERROR_MESSAGE_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, []);

  return {
    // Project state
    project,
    setProject,

    // Suggestion state
    suggestions,
    setSuggestions,
    isGeneratingSuggestions,
    setIsGeneratingSuggestions,
    isAcceptingAll,
    setIsAcceptingAll,
    suggestionProjectContext,
    setSuggestionProjectContext,
    adrSuggestionData,
    setAdrSuggestionData,

    // Generation state
    adrGenerationData,
    setAdrGenerationData,

    // Definition state
    definitions,
    setDefinitions,
    editingDefinition,
    setEditingDefinition,
    isLoadingDefinitions,
    setIsLoadingDefinitions,

    // Generating state
    isGeneratingADRs,
    setIsGeneratingADRs,
    generatingADRIds,
    setGeneratingADRIds,

    // Editor state
    editingADR,
    setEditingADR,
    adrContent,
    setAdrContent,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    lastSaved,
    setLastSaved,
    justSaved,
    setJustSaved,
    isSaving,
    setIsSaving,

    // Modal state
    showDefinitionModal,
    setShowDefinitionModal,
    showADRViewer,
    setShowADRViewer,
    showRegenerateConfirm,
    setShowRegenerateConfirm,
    regenerateADRId,
    setRegenerateADRId,

    // Error state
    errorMessage,
    setErrorMessage,
    showError,
    setShowError,
    showErrorMessage,

    // Form state
    formData,
    setFormData,
  };
};
